/**
 * Pagamentos em atraso, em Financeiro (spec 071): o que precisa de uma decisao sobre dinheiro de mensalidade. A parcela que
 * passou de 48 h, o comprovante em analise cuja parcela foi cancelada e o pagamento pela plataforma de parcela cancelada.
 * Cada linha diz a que se refere: cliente, plano e situacao dele, parcela, valor, atraso e comprovante.
 *
 * Quem entra, a tag, os dias de atraso e as decisoes que cabem vem prontos do backend: a tela so mostra e pergunta. Nada
 * aqui encerra, cancela ou cobra; encerrar, marcar paga e cancelar continuam na ficha do plano. So o ADMIN decide.
 */
import { somarDiasAoDia } from "@mtc/contratos";
import {
  Abas, Aviso, Botao, CabecalhoDePagina, CampoData, CampoTextoLongo, Carregando, Dialogo, LinkInterno, ListaDeEscolha, Pilha, Selo, Tabela, Vazio, VisorDeArquivo, formatarData, formatarDataHora, formatarDia, formatarReais, hojeEmSaoPaulo, type Coluna, type Tom,
} from "@mtc/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useSearchParams } from "react-router";
import { STATUS_DA_MATRICULA, erroDe, tituloDaParcela, useApi, usePapel, type DecisaoDoAtraso, type Motivos, type PagamentoEmAtraso } from "../api";
import { usePagina } from "../pagina";
import { useArquivo } from "./Comprovantes";
import { DialogoDeAcao } from "./DialogoDeAcao";

type Aba = "a-decidir" | "aguardando" | "fechados";
const ABAS: Aba[] = ["a-decidir", "aguardando", "fechados"];
const MOTIVO_MINIMO = 5;

const VAZIO: Record<Aba, { titulo: string; texto: string }> = {
  "a-decidir": { titulo: "Nada esperando decisão", texto: "Quando uma parcela passar de 48 h, ou um comprovante ou pagamento chegar pra uma parcela cancelada, ele aparece aqui." },
  aguardando: { titulo: "Nada aguardando", texto: "O que você decidir aguardar fica aqui até a data escolhida e depois volta para A decidir." },
  fechados: { titulo: "Nada fechado ainda", texto: "O que for decidido ou resolvido fica guardado aqui, com a decisão, o motivo e a data." },
};
const SITUACAO_DA_PARCELA: Record<NonNullable<PagamentoEmAtraso["parcela"]>["status"], string> = { A_PAGAR: "em aberto", PAGA: "paga", CANCELADA: "cancelada" };

const tomDaTag = (tag: string): Tom => (tag === "A decidir" ? "alerta" : tag === "Fechado" ? "neutro" : "info");
const dias = (n: number) => `${n} ${n === 1 ? "dia" : "dias"}`;
/** Sem quebrar entre o "R$" e o numero, nem entre "Mensalidade" e o numero dela. */
const semQuebra = (texto: string) => texto.replace(/ (?=\S+$)/, "\u00a0");

function useFila(aba: Aba) {
  const { ler } = useApi();
  return useQuery({ queryKey: ["pagamentos-em-atraso", aba], queryFn: () => ler<{ itens: PagamentoEmAtraso[] }>(`/v1/admin/pagamentos-em-atraso?aba=${aba}`) });
}

export function PaginaPagamentosEmAtraso() {
  const [params, setParams] = useSearchParams();
  const aba = (ABAS.includes(params.get("aba") as Aba) ? params.get("aba") : "a-decidir") as Aba;
  const { ler } = useApi();
  const { pode } = usePapel();
  const admin = pode("ADMIN");
  const lista = useFila(aba);
  const aDecidir = useFila("a-decidir");
  const aguardando = useFila("aguardando");
  // O motivo do encerramento vem como codigo: o catalogo do backend diz o nome, o mesmo da ficha.
  const motivos = useQuery({ queryKey: ["motivos"], queryFn: () => ler<Motivos>("/v1/admin/matriculas/motivos") });
  const pag = usePagina("pagina", [aba]);
  const [decidindo, setDecidindo] = useState<PagamentoEmAtraso | null>(null);
  const [abrindo, setAbrindo] = useState<PagamentoEmAtraso | null>(null);
  const [feito, setFeito] = useState<string>();
  // Le do endereco da janela: o do roteador chega numa transicao (o mesmo cuidado de Mensalidades).
  const mudarAba = (v: Aba) => {
    setFeito(undefined);
    const proximo = new URLSearchParams(window.location.search);
    if (v === "a-decidir") proximo.delete("aba");
    else proximo.set("aba", v);
    setParams(proximo, { replace: true });
  };
  const situacaoDoPlano = (p: NonNullable<PagamentoEmAtraso["plano"]>) => {
    const rotulo = STATUS_DA_MATRICULA[p.status].rotulo;
    if (p.status !== "ENCERRADA") return rotulo;
    const motivo = motivos.data?.encerramento.find((m) => m.codigo === p.motivo_encerramento)?.rotulo;
    return `${rotulo}${p.encerrado_em ? ` em ${formatarData(p.encerrado_em)}` : ""}${motivo ? `: ${motivo}` : ""}`;
  };
  // Nove colunas nao cabem em 1280 px com a lateral aberta: o tipo fica sob o cliente e o atraso sob o valor.
  const colunas: Coluna<PagamentoEmAtraso>[] = [
    { chave: "cliente", rotulo: "Cliente", celula: (x) => <>{x.plano ? <LinkInterno href={`/matriculas/${x.plano.matricula_id}`}>{x.pessoa?.nome ?? "Abrir o plano"}</LinkInterno> : x.pessoa?.nome ?? "Sem cadastro"}<span className="mtc-tabela__apoio">{x.tipo_rotulo}</span></> },
    { chave: "plano", rotulo: "Plano", celula: (x) => x.plano ? <>{x.plano.nome}<span className="mtc-tabela__apoio" title={x.plano.detalhe_encerramento ?? undefined}>{situacaoDoPlano(x.plano)}</span></> : "Sem plano" },
    { chave: "parcela", rotulo: "Parcela", celula: (x) => x.parcela ? <>{semQuebra(tituloDaParcela(x.parcela))}<span className="mtc-tabela__apoio"><span className="mtc-mono">{x.parcela.vencimento_formatado}</span>, {SITUACAO_DA_PARCELA[x.parcela.status]}</span></> : "Sem parcela" },
    { chave: "valor", rotulo: "Valor", celula: (x) => <><span className="mtc-mono">{x.valor_centavos != null ? formatarReais(x.valor_centavos) : "Sem valor"}</span>{Boolean(x.parcela?.dias_de_atraso) && <span className="mtc-tabela__apoio">{dias(x.parcela!.dias_de_atraso)} de atraso</span>}</> },
    { chave: "comprovante", rotulo: "Comprovante", celula: (x) => x.comprovante ? <><Botao variante="secundario" tamanho="pequeno" aria-label={`Abrir o comprovante de ${x.pessoa?.nome ?? "cliente"}`} onClick={() => setAbrindo(x)}>Abrir</Botao><span className="mtc-tabela__apoio">pago em <span className="mtc-mono">{x.comprovante.pago_no_dia_formatado}</span></span></> : <span className="mtc-texto-2">Sem comprovante</span> },
    ...(aba === "fechados" ? [{ chave: "decisao", rotulo: "Decisão", celula: (x: PagamentoEmAtraso) => <>{x.decisao_rotulo ?? "Sem decisão"}{x.motivo && <span className="mtc-tabela__apoio">{x.motivo}</span>}{x.decidido_em && <span className="mtc-tabela__apoio mtc-mono">{formatarDataHora(x.decidido_em)}</span>}</> }] : []),
    { chave: "situacao", rotulo: "Situação", celula: (x) => <><Selo tom={tomDaTag(x.tag)}>{x.tag}</Selo>{aba !== "fechados" && x.motivo && <span className="mtc-tabela__apoio">Motivo: {x.motivo}</span>}</> },
    ...(admin && aba !== "fechados" ? [{ chave: "acao", rotulo: "Decidir", rotuloOculto: true, celula: (x: PagamentoEmAtraso) => <Botao tamanho="pequeno" aria-label={`Decidir: ${x.pessoa?.nome ?? "cliente"}, ${x.tipo_rotulo}`} onClick={() => { setFeito(undefined); setDecidindo(x); }}>Decidir</Botao> }] : []),
  ];
  return (
    <Pilha espaco={5}>
      <CabecalhoDePagina titulo="Pagamentos em atraso" descricao="O que precisa de uma decisão sobre dinheiro de mensalidade. Nada aqui encerra, cancela ou cobra: isso continua na ficha do plano." />
      {feito && <Aviso tipo="sucesso">{feito}</Aviso>}
      {!admin && <Aviso tipo="info">Decidir estes pagamentos não está liberado no seu acesso. Você pode ver tudo.</Aviso>}
      <Abas
        rotulo="Pagamentos em atraso"
        valor={aba}
        aoMudar={mudarAba}
        abas={[
          { valor: "a-decidir", rotulo: "A decidir", contagem: aDecidir.data?.itens.length },
          { valor: "aguardando", rotulo: "Aguardando", contagem: aguardando.data?.itens.length },
          { valor: "fechados", rotulo: "Fechados" },
        ]}
      />
      {lista.isLoading ? <Carregando compacto texto="Buscando os pagamentos" /> : lista.isError ? <Aviso tipo="erro">{erroDe(lista.error)}</Aviso> : (
        <Tabela
          cartao="media"
          linhas={lista.data?.itens ?? []}
          chave={(x) => x.id}
          vazio={<Vazio titulo={VAZIO[aba].titulo}>{VAZIO[aba].texto}</Vazio>}
          colunas={colunas}
          paginacao={{ ...pag, rotulo: "Páginas dos pagamentos em atraso" }}
        />
      )}
      <DecidirPagamento x={decidindo} aoFechar={() => setDecidindo(null)} aoConcluir={(t) => { setFeito(t); window.scrollTo({ top: 0, behavior: "smooth" }); }} />
      <Dialogo aberto={Boolean(abrindo)} aoFechar={() => setAbrindo(null)} titulo="Comprovante" descricao={abrindo?.comprovante ? `${abrindo.pessoa?.nome ?? "Cliente"}, pago em ${abrindo.comprovante.pago_no_dia_formatado} segundo o cliente.` : undefined}>
        {abrindo?.comprovante && <ArquivoDoComprovante id={abrindo.comprovante.id} nome={abrindo.comprovante.arquivo} />}
      </Dialogo>
    </Pilha>
  );
}

/** Monta so com o dialogo aberto: cada abertura baixa o arquivo e fica na trilha, como na fila de Comprovantes. */
function ArquivoDoComprovante({ id, nome }: { id: string; nome: string }) {
  const arq = useArquivo(id);
  return <VisorDeArquivo url={arq.url} tipo={arq.tipo} nome={nome} carregando={arq.carregando} erro={arq.erro} />;
}

/** O que acontece depois de cada decisao, dito antes de salvar. Sem texto, a decisao so fecha a linha com o motivo. */
function explicacao(x: PagamentoEmAtraso, decisao: DecisaoDoAtraso | ""): string | null {
  if (decisao === "VALEU_PRA_OUTRO_PLANO") return "A baixa no outro plano é feita na ficha dele: marque a parcela paga lá. Aqui fica só o registro.";
  if (decisao === "DEVOLVIDO") return "A devolução é feita fora do sistema, pelo banco. Aqui fica só o registro.";
  if (decisao === "OUTRO" && x.tipo === "PARCELA_VENCIDA") return "Só fecha esta linha. A parcela segue em aberto: encerrar, marcar paga e cancelar ficam na ficha do plano.";
  return null;
}

function DecidirPagamento({ x, aoFechar, aoConcluir }: { x: PagamentoEmAtraso | null; aoFechar: () => void; aoConcluir: (t: string) => void }) {
  const { postar } = useApi();
  const qc = useQueryClient();
  const [decisao, setDecisao] = useState<DecisaoDoAtraso | "">("");
  const [ate, setAte] = useState("");
  const [motivo, setMotivo] = useState("");
  const aguardar = decisao === "AGUARDAR";
  // Comprovante preso fecha junto, sem baixa e sem aviso ao cliente (spec 071, 2.11).
  const fechaOComprovante = x?.tipo === "COMPROVANTE_DE_CANCELADA" && Boolean(decisao) && !aguardar;
  const nota = x ? explicacao(x, decisao) : null;
  return (
    <DialogoDeAcao
      aberto={Boolean(x)}
      aoFechar={aoFechar}
      titulo="Decidir o pagamento"
      descricao={x ? `${x.pessoa?.nome ?? "Cliente"}, ${x.plano?.nome ?? "sem plano"}. ${x.tipo_rotulo}${x.valor_centavos != null ? `, ${semQuebra(formatarReais(x.valor_centavos))}` : ""}.` : undefined}
      botao="Salvar decisão"
      podeEnviar={Boolean(decisao) && motivo.trim().length >= MOTIVO_MINIMO && (!aguardar || Boolean(ate))}
      aoAbrir={() => { setDecisao(""); setAte(""); setMotivo(""); }}
      aoEnviar={() => postar(`/v1/admin/pagamentos-em-atraso/${x!.id}/decidir`, { decisao, motivo: motivo.trim(), ...(aguardar ? { ate } : {}) })}
      aoConcluir={() => {
        void qc.invalidateQueries();
        aoConcluir(aguardar ? `Fica em Aguardando até ${formatarDia(ate)} e volta para A decidir depois dessa data.` : `Decisão salva: o pagamento foi para Fechados.${fechaOComprovante ? " O comprovante ficou conferido, sem baixa e sem aviso ao cliente." : ""}`);
      }}
    >
      <ListaDeEscolha rotulo="Decisão" nome="decisao-do-atraso" valor={decisao} aoMudar={(v) => setDecisao(v as DecisaoDoAtraso)} opcoes={(x?.decisoes ?? []).map((d) => ({ valor: d.valor, titulo: d.rotulo }))} />
      {aguardar && <CampoData rotulo="Aguardar até" valor={ate} aoMudar={setAte} min={somarDiasAoDia(hojeEmSaoPaulo(), 1)} required dica="Sai da fila até esta data e volta depois dela." />}
      {nota && <Aviso tipo="info">{nota}</Aviso>}
      {fechaOComprovante && <Aviso tipo="alerta">O comprovante fica como conferido, sem baixa e sem aviso ao cliente.</Aviso>}
      <CampoTextoLongo rotulo="Motivo" valor={motivo} aoMudar={setMotivo} maxLength={500} required placeholder="Ex.: conferi com o cliente pelo WhatsApp." dica="Pelo menos 5 letras. Fica na trilha da matrícula." />
    </DialogoDeAcao>
  );
}
